import {
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmDialog from "../../../components/ConfirmDialog";
import { memo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatUtcDateTime } from "../../../utils/dateTime";

const ENTITY_TYPE_COLOR = {
    CATEGORY: "primary",
    PRODUCT: "success",
    SKU: "warning",
};

const columns = [
    { id: "id", label: "ID" },
    { id: "entityId", label: "Entity ID" },
    { id: "entityName", label: "Entity Name" },
    { id: "entityType", label: "Entity Type" },
    { id: "discountPct", label: "Discount % on SP" },
    { id: "storeId", label: "Store ID" },
    { id: "timeCreated", label: "Created At" },
    { id: "timeUpdated", label: "Last Updated At" },
    { id: "actions", label: "Actions" },
];

// Memoized so opening/closing the delete-confirm dialog (local state) or a
// pagination change doesn't force every row to re-render its cells.
const DiscountRow = memo(function DiscountRow({
    row,
    storeName,
    timezone,
    onEdit,
    onConfirmDelete,
    navigate,
}) {
    return (
        <TableRow hover>
            <TableCell>{row.id ?? "-"}</TableCell>
            <TableCell>{row.entityId}</TableCell>
            <TableCell>{row.entityName ?? "-"}</TableCell>
            <TableCell>
                <Chip
                    label={row.entityType}
                    color={ENTITY_TYPE_COLOR[row.entityType] || "default"}
                    size="small"
                />
            </TableCell>
            <TableCell>{row.discountPct}%</TableCell>
            <TableCell>{`#${row.storeId} - ${storeName}`}</TableCell>
            <TableCell>
                {formatUtcDateTime(row.timeCreated, timezone)}
            </TableCell>
            <TableCell>
                {formatUtcDateTime(row.timeUpdated, timezone)}
            </TableCell>
            <TableCell>
                <IconButton size="small" onClick={() => onEdit(row)} color="primary">
                    <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onConfirmDelete(row)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
                {row.id && (
                    <Tooltip title="View History">
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label="history"
                            onClick={() => navigate(`/audit?entityId=${row.id}&entityType=FLASH_SALE_DISCOUNT`)}
                        >
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
});

export default function DiscountTableContainer({
    rows,
    page,
    size,
    loading,
    hasSearched,
    onPageChange,
    onRowsPerPageChange,
    onEdit,
    onDelete,
    storeName
}) {

    const [selectedRow, setSelectedRow] = useState(null);
    const timezone = useSelector((state) => state.globalData.timezone);
    const navigate = useNavigate();
    const paginatedRows = rows.slice(page * size, page * size + size);

    const onConfirmDelete = useCallback((row) => {
        setSelectedRow(row);
    }, []);

    const handleCloseConfirm = () => {
        setSelectedRow(null);
    }

    const handleConfirmDelete = () => {
        if (selectedRow) {
            onDelete(selectedRow.id);
        }
        handleCloseConfirm();
    }

    return (
        <Paper elevation={3}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            {columns.map((column) => (
                                <TableCell key={column.id} sx={{ fontWeight: 700 }}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedRows.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                    {hasSearched ? "No records found" : "Select a store and search"}
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows.map((row) => (
                                <DiscountRow
                                    key={row.id || `${row.storeId}-${row.entityType}-${row.entityId}`}
                                    row={row}
                                    storeName={storeName}
                                    timezone={timezone}
                                    onEdit={onEdit}
                                    onConfirmDelete={onConfirmDelete}
                                    navigate={navigate}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, nextPage) => onPageChange(nextPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) =>
                    onRowsPerPageChange(parseInt(event.target.value, 10))
                }
                rowsPerPageOptions={[5,10, 20, 50]}
            />

            <ConfirmDialog
                open={selectedRow !== null}
                title="Confirm Action"
                message={`Are you sure you want to delete this discount for entity ${selectedRow?.entityType} with Entity ID ${selectedRow?.entityId}?`}
                confirmText="Delete"
                confirmColor={"error"}
                loading={loading}
                onClose={handleCloseConfirm}
                onConfirm={handleConfirmDelete}
            />
        </Paper>
    );
}